import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import {injectIntl} from 'react-intl';
import {observer} from 'mobx-react';
import {action, observable, runInAction} from 'mobx';
import {Decoder, tools, Reader} from 'ts-ebml';
import FaChevronLeft from 'react-icons/lib/fa/chevron-left';
import FaStop from 'react-icons/lib/fa/stop';
import BaseButton from '../../../components/button/BaseButton';
import {VideoRecorderState} from '../../../constants/enums';
import SvgIconButton from '../../../components/SvgIconButton';
import {typoDark, white} from '../../../themes/colors';
import Video from '../../../assets/images/video-cam';

const btnStyle = {
    width: '48px',
    height: '48px',
    minWidth: '48px',
    minHeight: '48px',
    borderRadius: 0
};

const stopStyle = {
    width: '20px',
    height: '20px',
    color: white
};

const recordStyle = {
    color: white
};

@injectIntl
@observer
class LessonVideoRecorder extends Component {
    @observable recorderState = VideoRecorderState.Preview;
    @observable recorder = null;
    @observable interval = 0;
    @observable counter = 0;
    blob = null;

    @action componentWillUnmount() {
        if (this.recorder && this.recorder.state !== 'inactive') this.recorder.stop();
        this.props.stopStream();
    }

    @action componentDidMount() {
        this.playStream();
    }

    @action playStream = () => {
        this.videoNode.srcObject = this.props.stream;
        this.videoNode.play();
        this.videoNode.muted = true;
    };

    @action startRecordClick = async () => {
        this.recorderState = VideoRecorderState.Recording;
        setTimeout(() => { this.playStream(); }, 0);
        this.recorder = new MediaRecorder(this.props.stream, {
            mimeType: 'video/webm'
        });
        this.recorder.onstart = () => this.startCounter();
        this.recorder.start();
    };

    @action stopRecordingClick = () => {
        this.videoNode.srcObject = null;
        this.recorder.ondataavailable = async (e) => {
            this.blob = await this.injectMetadata(e.data);
            this.videoNode.src = URL.createObjectURL(this.blob);
        };
        this.recorderState = VideoRecorderState.Save;
        this.recorder.stop();
        clearInterval(this.interval);
        this.counter = 0;
    };

    readAsArrayBuffer = (blob) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsArrayBuffer(blob);
            reader.onloadend = () => { resolve(reader.result); };
            reader.onerror = (ev) => { reject(ev.error); };
        });
    };

    injectMetadata = async (blob) => {
        const decoder = new Decoder();
        const reader = new Reader();
        reader.logging = false;
        reader.drop_default_duration = false;

        const buffer = await this.readAsArrayBuffer(blob);
        const elms = decoder.decode(buffer);
        elms.forEach((elm) => { reader.read(elm); });
        reader.stop();

        const refinedMetadataBuf = tools.makeMetadataSeekable(reader.metadatas, reader.duration, reader.cues);
        const body = buffer.slice(reader.metadataSize);
        return new Blob([refinedMetadataBuf, body], {type: blob.type});
    };

    startCounter = () => {
        this.interval = setInterval(() => {
            runInAction(() => {
                this.counter += 1;
            });
        }, 1000);
    };

    submitVideo = () => {
        this.props.onSave(this.blob);
    };

    @action setRef = (node) => {
        this.videoNode = node;
    };

    renderRecordActions = () => {
        const {isSaveProcessing} = this.props;
        switch (this.recorderState) {
        case VideoRecorderState.Preview:
        case VideoRecorderState.Save:
            return <SvgIconButton
                disabled={isSaveProcessing || !this.props.stream}
                onClick={this.startRecordClick}
                viewBox="0 0 20 20"
                icon={Video}
                buttonStyle={btnStyle}
                hoverColor="none" rippleColor="none"
                iconStyle={recordStyle}
                containerClass="lesson-recorder-btn"/>;
        case VideoRecorderState.Recording:
            return <Fragment>
                <div className="lesson-recorder-counter">{`${`0${Math.floor(this.counter / 60)}`.slice(-2)}:${`0${this.counter % 60}`.slice(-2)}`}</div>
                <SvgIconButton
                    disabled={isSaveProcessing}
                    onClick={this.stopRecordingClick}
                    viewBox="0 0 12 12"
                    icon={<FaStop style={{fontSize: 12}}/>}
                    buttonStyle={btnStyle}
                    hoverColor="none" rippleColor="none"
                    iconStyle={stopStyle}
                    containerClass="lesson-recorder-btn"/>
            </Fragment>;
        default:
        }
        return null;
    };

    render() {
        const {toggleRecorder, isSaveProcessing, className, intl: {formatMessage: translate}} = this.props;
        return (
            <div className={`lesson-recorder ${className || ''}`}>
                <div className="lesson-recorder-close" onClick={toggleRecorder}>
                    <FaChevronLeft style={{fontSize: 13, color: typoDark}}/>
                </div>
                <div className="lesson-recorder-wrapper">
                    <div className="lesson-video-placeholder">
                        {this.recorderState !== VideoRecorderState.Save && <video autoPlay
                            ref={this.setRef}/>}
                        {this.recorderState === VideoRecorderState.Save && <video
                            controlsList="nodownload"
                            controls ref={this.setRef}/>}
                    </div>
                    <div className="lesson-video-actions">
                        {this.renderRecordActions()}
                        <BaseButton className="lesson-btn"
                            label={translate({id: 'createLesson.saveVideo'})}
                            disabled={isSaveProcessing || this.recorderState !== VideoRecorderState.Save}
                            onClick={this.submitVideo} />
                    </div>
                </div>
            </div>
        );
    }
}

LessonVideoRecorder.propTypes = {
    toggleRecorder: PropTypes.func,
    onSave: PropTypes.func,
    stopStream: PropTypes.func,
    stream: PropTypes.object,
    isSaveProcessing: PropTypes.bool,
    className: PropTypes.string
};

export default LessonVideoRecorder;
